import { EMPTY_ARRAY } from '@unifyapps/carbon/consts/empty';
import type { BlockType } from '@unifyapps/defs/types/block';
import { isRuntimeSourceBlock } from '../../utils/runTimeBlocks';

/**
 * Block id → inner-first list of runtime-source ancestors (Repeatables,
 * dynamic Carousel/SwipeableStack, TreeView). Excludes the block itself.
 * Blocks with no runtime-source ancestor are NOT in the map — Map.get returns
 * undefined for an O(1) early-return on the dep-graph notify hot path.
 */
export type RepeatableAncestorMap = ReadonlyMap<string, readonly string[]>;

const EMPTY_ANCESTORS = EMPTY_ARRAY as readonly string[];

function getAncestorsForRepeatableMap(
  id: string,
  blocks: Record<string, BlockType | undefined>,
  work: Map<string, readonly string[]>,
): readonly string[] {
  const cached = work.get(id);
  if (cached !== undefined) return cached;

  const block = blocks[id];
  if (!block) {
    work.set(id, EMPTY_ANCESTORS);
    return EMPTY_ANCESTORS;
  }

  const parentId = block.parentId;
  const parent = parentId ? blocks[parentId] : undefined;
  if (!parent || !parentId) {
    work.set(id, EMPTY_ANCESTORS);
    return EMPTY_ANCESTORS;
  }

  const parentAncestors = getAncestorsForRepeatableMap(parentId, blocks, work);
  // allocate only at runtime-source boundaries; non-Repeatable containers
  // share the parent array by reference
  const ancestors: readonly string[] = isRuntimeSourceBlock(parent)
    ? [parentId, ...parentAncestors]
    : parentAncestors;

  work.set(id, ancestors);
  return ancestors;
}

/**
 * O(N) build with structural sharing: a block whose parent is a runtime-source
 * gets a fresh `[parentId, ...parentAncestors]`; every other block reuses the
 * parent's array by reference. Empty entries are stripped so common-case
 * lookups miss with a single `Map.get → undefined`.
 */
export function computeRepeatableAncestorMap(
  blocks: Record<string, BlockType | undefined>,
): RepeatableAncestorMap {
  // memoize empty results too so chains that hit a non-Repeatable root
  // aren't re-walked; empty entries are dropped before returning
  const work = new Map<string, readonly string[]>();

  for (const id of Object.keys(blocks)) {
    if (blocks[id]) getAncestorsForRepeatableMap(id, blocks, work);
  }

  const final = new Map<string, readonly string[]>();
  for (const [id, ancestors] of work) {
    if (ancestors.length > 0) final.set(id, ancestors);
  }
  return final;
}
